import { useLang } from '@/i18n';
import { useEffect, useRef, useState } from 'react';
import css from './index.module.scss';

const I: React.FC = () => {
    const { t } = useLang('stream_ultra_x');
    const [enter, setEnter] = useState<boolean>(false);
    const [loadImg, setLoadImg] = useState<boolean>(false);
    const imgMap: Record<string, HTMLImageElement> = {};
    const boxRef = useRef<HTMLElement>(null);
    const splitTimesA = 10; //滚动触发的间隔;毫秒
    const throttlePlayingRefA = useRef<number>(Date.now()); //用于少点移动

    const checkWebPSupport = (callback: (isWebPSupported: boolean) => void) => {
        const webP = new Image();
        // 这是一个 内联的 Base64 编码的 WebP 图片，无需额外网络请求。
        // 图片尺寸仅为 2x1 像素（极小，仅用于检测格式支持）。
        webP.src = 'data:image/webp;base64,UklGRjoAAABXRUJQVlA4IC4AAACyAgCdASoCAAIALmk0mk0iIiIiIgBoSygABc6WWgAA/veff/0PP8bA//LwYAAA';
        webP.onload = webP.onerror = () => {
            callback(webP.height === 2);
        };
    };

    const loadImages = async (useWebP: boolean) => {
        const totalFrames = 89;
        const isM = window.innerWidth < 768;
        for (let i = 1; i <= totalFrames; i++) {
            const prefix = useWebP ? 'frame_0' : `ULTRA_X_Expandable_${isM ? 'M' : 'WEB'}_`;
            const suffix = useWebP ? '.webp' : '-tuya.jpg';
            // const prefix = 'ULTRA_X_Expandable_WEB_';
            // const suffix = '-tuya.jpg';
            const frameNum = String(i).padStart(5, '000');
            const url = `https://websiteoss.ecoflow.com/upload/sequence-frame/stream-series-1/${isM ? 'M' : 'PC'}/${prefix}${frameNum}${suffix}`;
            loadSingleImage(url, frameNum);
        }
    };

    const loadSingleImage = async (src: string, frameNum: string) => {
        return new Promise<HTMLImageElement>((resolve) => {
            const img = new Image();
            img.src = src;
            img.width = window.innerWidth;
            img.height = window.innerHeight;
            img.onload = () => resolve(img);
            imgMap[frameNum] = img;
        });
    };

    const renderCanvasImg = (curImg: HTMLImageElement) => {
        const canvas = document.getElementById('video-canvas-I') as HTMLCanvasElement;
        if (!canvas || !curImg) return;

        // 1. 获取逻辑尺寸（CSS像素）和物理尺寸
        const dpr = window.devicePixelRatio || 1;
        const logicalWidth = canvas.clientWidth;
        const logicalHeight = canvas.clientHeight;

        // 2. 设置Canvas物理分辨率（与CSS尺寸分离）
        canvas.width = Math.round(logicalWidth * dpr);
        canvas.height = Math.round(logicalHeight * dpr);

        const ctx = canvas.getContext('2d')!;

        // 3. 重置变换矩阵（重要！）
        ctx.resetTransform();

        // 4. 计算cover模式的绘制参数（基于逻辑尺寸）
        const imgRatio = curImg.naturalWidth / curImg.naturalHeight;
        const canvasRatio = logicalWidth / logicalHeight;

        let drawWidth, drawHeight, offsetX, offsetY;

        if (imgRatio > canvasRatio) {
            // 宽图：按高度裁切
            drawHeight = logicalHeight;
            drawWidth = drawHeight * imgRatio;
            offsetX = (logicalWidth - drawWidth) / 2;
            offsetY = 0;
        } else {
            // 高图：按宽度裁切
            drawWidth = logicalWidth;
            drawHeight = drawWidth / imgRatio;
            offsetX = 0;
            offsetY = (logicalHeight - drawHeight) / 2;
        }

        // 5. 清除画布（使用物理尺寸）
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        // 6. 绘制到物理坐标系（自动适配DPI）
        ctx.drawImage(
            curImg,
            0,
            0,
            curImg.naturalWidth,
            curImg.naturalHeight, // 源图完整尺寸
            offsetX * dpr,
            offsetY * dpr, // 物理坐标
            drawWidth * dpr,
            drawHeight * dpr, // 物理尺寸
        );
    };

    useEffect(() => {
        if (loadImg) {
            checkWebPSupport((isWebPSupported) => {
                loadImages(isWebPSupported);
            });
            setTimeout(() => {
                renderCanvasImg(imgMap['00001']);
                const handleScroll = () => {
                    const nowTimesA = Date.now();
                    const nextTimeA = throttlePlayingRefA.current + splitTimesA;
                    if (nowTimesA < nextTimeA) {
                        // 现在的时间小于(上次的时间加个时间间隔)
                        return;
                    }
                    throttlePlayingRefA.current = nowTimesA;

                    const scrollY = window.scrollY;
                    const canvas = document.getElementById('video-canvas-I') as HTMLCanvasElement;
                    const section = boxRef.current;
                    if (!canvas || !section) return;

                    // 计算动画播放区间（从元素进入视口到完全离开）
                    const playHeight = window.innerHeight;
                    const startScroll = section.offsetTop;
                    const endScroll = startScroll + playHeight;
                    // 如果滚动位置在动画区间内
                    if (scrollY >= startScroll && scrollY <= endScroll) {
                        // 计算当前帧索引（0 ~ 88）
                        const progress = (scrollY - startScroll) / playHeight;
                        const frameIndex = String(Math.min(Math.floor(progress * 89), 89 - 1)).padStart(5, '000');
                        if (imgMap[frameIndex + '']) {
                            renderCanvasImg(imgMap[frameIndex + '']);
                        }
                    }
                };
                window.addEventListener('scroll', handleScroll);
                return () => {
                    window.removeEventListener('scroll', handleScroll);
                };
            }, 1000);
        }
    }, [loadImg]);

    useEffect(() => {
        if (typeof window !== 'undefined') {
            const isM = window.innerWidth < 768;
            const sceneI1 = AniContainer.create({
                triggerElement: `.stream-ultra-x-C`,
                triggerHook: 1,
                onEnter: () => setLoadImg(true),
            });

            const sceneI2 = AniContainer.create({
                triggerElement: `.stream-ultra-x-I`,
                triggerHook: 1,
                offset: isM ? '220%' : '180%',
                duration: isM ? '180%' : '120%',
                onEnter: () => setEnter(true),
                onLeave: () => setEnter(false),
            });

            return () => {
                sceneI1.destroy();
                sceneI2.destroy();
            };
        }
    }, []);

    return (
        <section
            className={`${css.section} stream-ultra-x-I GtmModuleData-section`}
            id="stream-ultra-x-I"
            data-section-id={'stream-ultra-x-I'}
            data-section-title={t('I_title')}
            ref={boxRef}
        >
            <div className="sticky">
                <canvas className="video canvas" id="video-canvas-I"></canvas>
                <div className={`info${enter ? ' enter' : ''}`}>
                    <p className="title">{t('I_title')}</p>
                    <p className="desc">{t('I_desc')}</p>
                    <ul className="items">
                        {[1, 2].map((i: number) => (
                            <li className="item" key={i}>
                                <p className="item-title">{t(`I_item${i}_title`)}</p>
                                <p className="item-desc">{t(`I_item${i}_desc`)}</p>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="position-block" id="stream-ultra-x-I-position"></div>
        </section>
    );
};

export default I;
